import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { useTheme } from '@app/theme/ThemeContext';

interface LocationSearchProps {
  onLocationSelect: (lat: number, lon: number, name: string) => void;
}

interface Suggestion {
  place_name: string;
  center: [number, number];
}

const MAPBOX_TOKEN =
  'pk.eyJ1IjoidGluYXVra2VsaSIsImEiOiJjbTNvY3o0Y3kwMXVqMm1zZTVna254OGV1In0.IljVd6sN8o__c0yKzoAs7g';

export default function LocationSearch({ onLocationSelect }: LocationSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?` +
            new URLSearchParams({
              access_token: MAPBOX_TOKEN,
              types: 'place',
              limit: '5',
            }),
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSuggestions(data.features);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleLocationSelect = (suggestion: Suggestion) => {
    // Immediately close dropdown and clear search
    setShowSuggestions(false);
    setSearchQuery('');

    // Call the location select callback
    onLocationSelect(suggestion.center[1], suggestion.center[0], suggestion.place_name);
  };

  return (
    <div
      className="absolute top-4 left-4 w-80 max-w-[calc(100vw-8rem)] sm:max-w-none z-20"
      ref={searchRef}
    >
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
        </div>
        <input
          type="text"
          placeholder="Search for places..."
          className={`block w-full pl-10 pr-3 py-3 border rounded-xl text-sm font-medium transition-all duration-200 ${
            isDark
              ? 'border-gray-600 bg-gray-800/95 backdrop-blur text-gray-100 placeholder-gray-400 focus:border-pink-400/50 focus:ring-pink-400/20'
              : 'border-gray-200 bg-white/95 backdrop-blur text-gray-900 placeholder-gray-500 focus:border-pink-500/50 focus:ring-pink-500/20'
          } focus:outline-none focus:ring-4 shadow-lg`}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowSuggestions(true);
          }}
        />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div
          className={`absolute z-30 mt-2 w-full shadow-2xl rounded-xl py-2 border backdrop-blur ${
            isDark ? 'bg-gray-800/95 border-gray-600' : 'bg-white/95 border-gray-200'
          }`}
        >
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_name}
              className={`w-full px-4 py-3 text-left text-sm font-medium transition-all duration-150 ${
                isDark
                  ? 'hover:bg-pink-500/10 hover:text-pink-300 text-gray-100'
                  : 'hover:bg-pink-50 hover:text-pink-700 text-gray-900'
              }`}
              onClick={() => handleLocationSelect(suggestion)}
            >
              {suggestion.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
